import * as turf from "@turf/turf";
import type { Geometry } from "geojson";
import type mapboxgl from "mapbox-gl";
import { useCallback, useEffect, type FC } from "react";
import {
  EXISTING_FIELD_NAME_LAYER_ID,
  FIELD_LABELS_LAYER_ID,
  FIELD_LABELS_SOURCE_ID,
  type FieldData,
} from "../MapControlsPanel.types";

interface FieldNamesToolProps {
  mapRef: mapboxgl.Map | null;
  isEnabled: boolean;
  currentFieldId?: string;
  fields?: FieldData[];
}

const CURRENT_FIELD_COLOR = "#FFFFFF";
const NEIGHBORING_FIELD_COLOR = "#C1C5D0";
const CURRENT_FIELD_TEXT_SIZE = 16;
const NEIGHBORING_FIELD_TEXT_SIZE = 13;

/**
 * AC: the current field is highlighted and neighbouring fields use a neutral
 * colour. Driven off whichever property the underlying source carries.
 */
const highlightPaint = (
  flagExpression: mapboxgl.Expression
): { "text-color": mapboxgl.Expression } => ({
  "text-color": [
    "case",
    flagExpression,
    CURRENT_FIELD_COLOR,
    NEIGHBORING_FIELD_COLOR,
  ],
});

const highlightTextSize = (
  flagExpression: mapboxgl.Expression
): mapboxgl.Expression => [
  "case",
  flagExpression,
  CURRENT_FIELD_TEXT_SIZE,
  NEIGHBORING_FIELD_TEXT_SIZE,
];

/** Label anchor for a field — point-on-feature keeps the text inside odd shapes. */
const centerOf = (geometry: Geometry): number[] | null => {
  try {
    // turf v6 types exclude nested GeometryCollection; the runtime handles it.
    const point = turf.pointOnFeature(turf.feature(geometry) as any);
    const coordinates = point?.geometry?.coordinates;
    return Array.isArray(coordinates) && coordinates.length >= 2
      ? coordinates
      : null;
  } catch (error) {
    console.warn("Could not compute field label position:", error);
    return null;
  }
};

const FieldNamesTool: FC<FieldNamesToolProps> = ({
  mapRef,
  isEnabled,
  currentFieldId,
  fields,
}) => {
  /**
   * The trial map already labels every field through `polygon-name`. Reuse it
   * rather than stacking a second symbol layer on top, which would double every
   * label on the map.
   */
  const applyToExistingLayer = useCallback(
    (map: mapboxgl.Map): boolean => {
      if (!map.getLayer(EXISTING_FIELD_NAME_LAYER_ID)) {
        return false;
      }

      map.setLayoutProperty(
        EXISTING_FIELD_NAME_LAYER_ID,
        "visibility",
        isEnabled ? "visible" : "none"
      );

      if (isEnabled) {
        const isSelected: mapboxgl.Expression = ["get", "selected"];
        map.setPaintProperty(
          EXISTING_FIELD_NAME_LAYER_ID,
          "text-color",
          highlightPaint(isSelected)["text-color"]
        );
        map.setLayoutProperty(
          EXISTING_FIELD_NAME_LAYER_ID,
          "text-size",
          highlightTextSize(isSelected)
        );
      }

      return true;
    },
    [isEnabled]
  );

  /** Fallback for maps that do not draw field names themselves. */
  const applyOwnLayer = useCallback(
    (map: mapboxgl.Map): void => {
      if (!fields?.length) {
        return;
      }

      const features = fields
        .filter((field) => field.geometry)
        .map((field) => ({
          coordinates: centerOf(field.geometry as Geometry),
          field,
        }))
        .filter(({ coordinates }) => coordinates !== null)
        .map(({ coordinates, field }) => ({
          type: "Feature" as const,
          geometry: {
            type: "Point" as const,
            coordinates: coordinates as number[],
          },
          properties: {
            name: field.name,
            id: field.id,
            isCurrentField: field.id === currentFieldId,
          },
        }));

      const geojson: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features,
      };

      const existingSource = mapRef?.getSource(FIELD_LABELS_SOURCE_ID);
      if (existingSource?.type === "geojson") {
        existingSource.setData(geojson);
      } else if (!existingSource) {
        map.addSource(FIELD_LABELS_SOURCE_ID, {
          type: "geojson",
          data: geojson,
        });
      }

      const isCurrent: mapboxgl.Expression = ["get", "isCurrentField"];

      if (!map.getLayer(FIELD_LABELS_LAYER_ID)) {
        map.addLayer({
          id: FIELD_LABELS_LAYER_ID,
          type: "symbol",
          source: FIELD_LABELS_SOURCE_ID,
          layout: {
            "text-field": ["get", "name"],
            "text-size": highlightTextSize(isCurrent),
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          },
          paint: {
            ...highlightPaint(isCurrent),
            "text-halo-color": "#14151C",
            "text-halo-width": 1.5,
          },
        });
      }

      map.setLayoutProperty(
        FIELD_LABELS_LAYER_ID,
        "visibility",
        isEnabled ? "visible" : "none"
      );
    },
    [mapRef, fields, currentFieldId, isEnabled]
  );

  useEffect(() => {
    if (!mapRef) {
      return;
    }

    const applyFieldNames = (): void => {
      if (!applyToExistingLayer(mapRef)) {
        applyOwnLayer(mapRef);
      }
    };

    if (mapRef.isStyleLoaded()) {
      applyFieldNames();
    }

    // Re-apply after a base layer switch replaces the style.
    const handleStyleData = (): void => {
      if (mapRef.isStyleLoaded()) {
        applyFieldNames();
      }
    };

    mapRef.on("styledata", handleStyleData);

    return () => {
      try {
        mapRef.off("styledata", handleStyleData);
        if (mapRef.getLayer?.(FIELD_LABELS_LAYER_ID)) {
          mapRef.removeLayer(FIELD_LABELS_LAYER_ID);
        }
        if (mapRef.getSource?.(FIELD_LABELS_SOURCE_ID)) {
          mapRef.removeSource(FIELD_LABELS_SOURCE_ID);
        }
      } catch {
        // Expected while the map is unmounting.
      }
    };
  }, [mapRef, applyToExistingLayer, applyOwnLayer]);

  return null;
};

export default FieldNamesTool;
